import { api } from '../apiClient';

/**
 * 서비스 로그인 연동(OIDC 클라이언트) — 슈퍼관리자용 (#134)
 *
 * 바깥 서비스가 콘솔 계정으로 사용자를 로그인시킬 때 쓰는 client_id · client_secret.
 * 서버 간 호출용 API 키(MSAService.api_key)와는 쓰임이 다르다.
 *
 * `client_secret` 은 **발급·재발급할 때 한 번만** 응답에 담긴다. 서버는 해시만 갖고
 * 있어 다시 보여줄 수 없다 — 잃어버리면 재발급해야 한다.
 */

/** 발급·재발급 응답. secret 이 들어 있는 유일한 자리 */
export interface IssuedClient {
  service_slug: string;
  client_id: string;
  client_secret: string;
  redirect_uris: string[];
  note: string;
}

/** 조회 응답. secret 은 없다 (백엔드 ClientView 와 같은 모양) */
export interface ServiceClient {
  service_slug: string;
  client_id: string;
  redirect_uris: string[];
  is_active: boolean;
}

export const serviceClientAPI = {
  /** 발급. 이미 살아 있는 클라이언트가 있으면 409 — 그때는 재발급을 쓴다 */
  issue: (serviceSlug: string, redirectUris: string[]): Promise<IssuedClient> =>
    api
      .post(`/admin/services/${serviceSlug}/client`, { redirect_uris: redirectUris })
      .then((response) => response.data),

  /** 현재 클라이언트. 발급 전이면 404 */
  get: (serviceSlug: string): Promise<ServiceClient> =>
    api.get(`/admin/services/${serviceSlug}/client`).then((response) => response.data),

  /** secret 만 새로 발급한다. **옛 secret 은 즉시 못 쓴다** */
  reissue: (serviceSlug: string): Promise<IssuedClient> =>
    api
      .post(`/admin/services/${serviceSlug}/client/reissue`)
      .then((response) => response.data),

  /** 콜백 주소 변경. 정확히 일치할 때만 받으므로 글자 하나까지 맞춰야 한다 */
  updateRedirectUris: (serviceSlug: string, redirectUris: string[]): Promise<ServiceClient> =>
    api
      .put(`/admin/services/${serviceSlug}/client/redirect-uris`, { redirect_uris: redirectUris })
      .then((response) => response.data),

  /** 폐기. 이 서비스로는 더 이상 로그인할 수 없다 */
  revoke: (serviceSlug: string): Promise<ServiceClient> =>
    api.delete(`/admin/services/${serviceSlug}/client`).then((response) => response.data),
};

/**
 * 서비스별 로그인 연동 현황 — 슈퍼관리자용 (#219)
 *
 * 상태는 **백엔드가 판정해서** 내려준다(services/oidc/integrations.py). 화면은 판정하지 않는다.
 */
export type IntegrationStatus = 'not_issued' | 'revoked' | 'no_traffic' | 'failing' | 'healthy';

export interface IntegrationFailureReason {
  step: 'authorize' | 'token';
  reason: string;
  count: number;
  last_at: string;
}

export interface Integration {
  service_slug: string;
  service_name: string;
  service_status: string;
  service_url: string | null;
  client: {
    client_id: string;
    is_active: boolean;
    redirect_uris: string[];
    created_at: string | null;
    last_used_at: string | null;
  } | null;
  last_login_at: string | null;
  stats: {
    authorize_success: number;
    token_success: number;
    failures: number;
    /** 콘솔에서 코드는 받았는데 서비스가 토큰으로 바꾸지 못한 횟수 */
    unfinished: number;
    last_failure_at: string | null;
    failure_reasons: IntegrationFailureReason[];
  };
  status: IntegrationStatus;
}

export const oidcIntegrationAPI = {
  list: (days: number): Promise<{ days: number; integrations: Integration[] }> =>
    api.get('/admin/oidc/integrations', { params: { days } }).then((response) => response.data),
};
